import { Checkbox, Radio, Select, Space } from "antd";
import { Form, Formik } from "formik";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { getObjectivesWithAlertes } from "../../../../data/slices/objectives";
import {
  exportSynthesisResult,
  resetExportStatus,
} from "../../../../data/slices/exports";
import { closeModal } from "../../../../data/slices/modals";
import { parseDate } from "../../../../utilities/filters";
import { transformFilterValues } from "../../../../hooks/useFiltersAndDate";
import Button from "../../../Button";
import Overlay from "../../../Overlay";
import HeaderOverlay from "../../../Project/Header";
import Title from "../../../Project/Title";
import CustomRangePicker from "../../../RangePicker";
import "./index.scss";

const { Option } = Select;

function ExportSynthesisResult({ open, onCancel, onContinue, data, ...rest }) {
  const dispatch = useDispatch();

  // Form validation schema
  const ExportSchema = Yup.object().shape({
    dateRange: Yup.array()
      .of(Yup.mixed())
      .length(2, "Veuillez sélectionner une période")
      .required("Veuillez sélectionner une période"),
    levelType: Yup.string().required("Veuillez sélectionner un type de niveau"),
    categoryLevels: Yup.number().test(
      "categoryLevels-custom",
      "Veuillez sélectionner le nombre de niveaux",
      function (value) {
        // If levelType is "custom", categoryLevels must be required and between 0 and 3
        if (this.parent.levelType === "custom") {
          if (typeof value !== "number" || isNaN(value)) return false;
          if (value < 1)
            return this.createError({ message: "Minimum 0 niveau" });
          if (value > 5)
            return this.createError({ message: "Maximum 5 niveaux" });
          return true;
        }
        // If not custom, categoryLevels is not required
        return true;
      }
    ),
    budgetType: Yup.string().required(
      "Veuillez sélectionner un type de budget"
    ),
    budgetId: Yup.number().when("budgetType", {
      is: (val) => val === "specific",
      then: (schema) => schema.required("Veuillez sélectionner un budget"),
      otherwise: (schema) => schema.notRequired(),
    }),
    filterType: Yup.string().required(
      "Veuillez sélectionner un type de filtre"
    ),
    selectedFilters: Yup.array().when("filterType", {
      is: "apply",
      then: (schema) =>
        schema.min(1, "Veuillez sélectionner au moins un filtre"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const { objectivesWithAlertes } = useSelector((state) => state.objectives);
  const { exportStatus, lastExportResult } = useSelector(
    (state) => state.exports
  );
  const { selectedMonths } = useSelector((state) => state.layout);

  const getStartAndEndDateSideBar = () => {
    const start = selectedMonths?.map((month) => parseDate(month))[0];
    const end = selectedMonths?.map((month) => parseDate(month))[
      selectedMonths?.length - 1
    ];

    return { start, end };
  };

  const { start, end } = getStartAndEndDateSideBar();

  // Initial form values
  const initialValues = useMemo(
    () => ({
      dateRange: [start, end], // Date range for the picker
      levelType: "all", // "all" for "Tous les niveaux", "custom" for dropdown option
      categoryLevels: 1,
      budgetType: "none", // "none" for "Aucun budget", "specific" for dropdown option
      budgetId: null,
      filterType: "none", // "none" for "Aucun filtre", "apply" for "Appliquer un filtre"
      selectedFilters: [],
      excludeMaskedEmptyCategories: true,
    }),
    [start, end]
  );

  // Filter data - similar to FilteringCompta
  const filterData = [
    {
      id: 2,
      label: "Réelle",
      children: [
        {
          id: 21,
          label: "soldée",
          background: "#a9efe5",
          filterValues: { status: "pointé", type: "réel" },
        },
        {
          id: 22,
          label: "non-soldée",
          background: "#e6fbf8",
          filterValues: { status: "engagé,prévisionnel", type: "réel" },
        },
      ],
    },
    {
      id: 3,
      label: "simul",
      filterValues: { type: "simulation" },
    },
  ];

  useEffect(() => {
    if (open && data?.projectId) {
      dispatch(
        getObjectivesWithAlertes({
          projectId: data.projectId,
          isPlancompta: false,
        })
      );
    }
  }, [dispatch, data?.projectId, open]);

  // Reset export status when modal opens
  useEffect(() => {
    if (open) {
      dispatch(resetExportStatus());
    }
  }, [open, dispatch]);

  // Handle export success/error
  useEffect(() => {
    if (exportStatus === "succeeded" && lastExportResult) {
      // File download is handled automatically in the slice
      console.log("Export completed successfully");
    }
  }, [exportStatus, lastExportResult]);

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);

    try {
      // Format dates for API
      const startDate = values.dateRange?.[0]?.format("YYYY-MM");
      const endDate = values.dateRange?.[1]?.format("YYYY-MM");

      const at = new Date().toISOString();

      let maxCategoryDepth;
      if (values.levelType === "all") {
        maxCategoryDepth = 5;
      } else {
        maxCategoryDepth = values.categoryLevels;
      }

      const filterOptions =
        values.filterType === "apply"
          ? transformFilterValues(values.selectedFilters)
          : [];

      const exportData = {
        startDate,
        endDate,
        maxCategoryDepth,
        at,
        filterOptions,
        objective: values.budgetType === "specific" ? values.budgetId : null,
        excludeMaskedEmptyCategories: values.excludeMaskedEmptyCategories,
      };

      await dispatch(
        exportSynthesisResult({
          projectId: data?.projectId,
          tableType: "categories_and_plan_comptable",
          mode: "compta",
          exportData,
        })
      ).then((res) => {
        if (res.meta.requestStatus === "rejected") {
          return;
        } else {
          // Reset form values to initial state after successful export
          resetForm({
            values: {
              dateRange: [start, end],
              levelType: "all",
              categoryLevels: 5,
              budgetType: "none",
              budgetId: null,
              filterType: "none",
              selectedFilters: [],
              excludeMaskedEmptyCategories: true,
            },
          });
          dispatch(closeModal("export-synthesis-result-modal"));
        }
      });
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (resetForm) => {
    // Reset form values when canceling
    if (resetForm) {
      resetForm({
        values: {
          dateRange: [start, end],
          levelType: "all",
          categoryLevels: 5,
          budgetType: "none",
          budgetId: null,
          filterType: "none",
          selectedFilters: [],
          excludeMaskedEmptyCategories: true,
        },
      });
    }

    if (typeof onCancel === "function") {
      onCancel();
    }
  };

  // Helper functions for filter handling (similar to FilteringCompta)
  const handleFilterChange = (
    filter,
    checked,
    selectedFilters,
    setFieldValue
  ) => {
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

    const updatedFilters = checked
      ? [...selectedFilters, filter.filterValues]
      : selectedFilters.filter(
          (selected) => !isEqual(selected, filter.filterValues)
        );

    setFieldValue("selectedFilters", updatedFilters);
  };

  const handleParentFilterChange = (
    parent,
    checked,
    selectedFilters,
    setFieldValue
  ) => {
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

    const childValues = parent.children
      ? parent.children.map((child) => child.filterValues)
      : [];

    if (checked) {
      const filtersToAdd = [parent.filterValues, ...childValues].filter(
        Boolean
      );
      const newFilters = [...selectedFilters];

      filtersToAdd.forEach((filter) => {
        if (!newFilters.some((existing) => isEqual(existing, filter))) {
          newFilters.push(filter);
        }
      });

      setFieldValue("selectedFilters", newFilters);
    } else {
      const updatedFilters = selectedFilters.filter(
        (selected) =>
          !isEqual(selected, parent.filterValues) &&
          !childValues.some((childValue) => isEqual(selected, childValue))
      );

      setFieldValue("selectedFilters", updatedFilters);
    }
  };

  const isParentChecked = (parent, selectedFilters) => {
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    const { children } = parent;
    if (!children || children.length === 0) return false;

    return children.every((child) =>
      selectedFilters.some((selected) => isEqual(selected, child.filterValues))
    );
  };

  return (
    <Overlay show={open} closable={true}>
      <HeaderOverlay showlogo className={"export-synthesis-result-overlay"}>
        <Title
          className={"export-synthesis-result-title"}
          isDelete
          subTitle={`Export d'une synthèse du résultat`}
        />

        <Formik
          initialValues={initialValues}
          validationSchema={ExportSchema}
          onSubmit={handleFormSubmit}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleSubmit,
            resetForm,
          }) => {
            return (
              <Form className="export-synthesis-form" onSubmit={handleSubmit}>
                <div className="export-synthesis-content">
                  <div className="export-synthesis-content-wrapper">
                    <div className="export-synthesis-section">
                      <div className="section-wrapper-header">
                        <div className="section-number">1)</div>
                        <div className="section-title">
                          Choisissez la période à exporter:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="date-range-container">
                          <CustomRangePicker
                            selectedMonthRange={values.dateRange}
                            handleMonthChange={(dateRange, saveDateChecked) => {
                              setFieldValue("dateRange", dateRange);
                            }}
                            className={"custom-range-picker-synthese"}
                            isSyntheseSelected={false}
                            label="Période"
                            index={0}
                            hasDivider={true}
                          />
                          {errors.dateRange && touched.dateRange && (
                            <div className="error-message">
                              {errors.dateRange}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="export-synthesis-section">
                      <div className="section-wrapper-header">
                        <div className="section-number">2)</div>
                        <div className="section-title">
                          Choisissez jusqu'à combien de niveaux de
                          sous-catégories vous souhaitez exporter votre
                          synthèse:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="radio-options-container">
                          <Radio.Group
                            onChange={(e) => {
                              setFieldValue("levelType", e.target.value);
                              // Reset categoryLevels to 0 if switching to "all"
                              if (e.target.value !== "custom") {
                                setFieldValue("categoryLevels", 1);
                              }
                            }}
                            value={values.levelType}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            <Radio value="all">Tous les niveaux</Radio>

                            <Space>
                              <Radio value="custom" />
                              <Select
                                value={String(values.categoryLevels)}
                                disabled={values.levelType !== "custom"}
                                onChange={(val) =>
                                  setFieldValue("categoryLevels", Number(val))
                                }
                                style={{ minWidth: 300 }}
                              >
                                <Option value="1">
                                  Uniquement les catégories principales
                                </Option>
                                <Option value="2">
                                  avec 1 niveau de sous-catégories
                                </Option>
                                <Option value="3">
                                  avec 2 niveaux de sous-catégories
                                </Option>
                                <Option value="4">
                                  avec 3 niveaux de sous-catégories
                                </Option>
                              </Select>
                            </Space>
                          </Radio.Group>

                          {errors.levelType && touched.levelType && (
                            <div className="error-message">
                              {errors.levelType}
                            </div>
                          )}
                          {errors.categoryLevels && touched.categoryLevels && (
                            <div className="error-message">
                              {errors.categoryLevels}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="export-synthesis-section">
                      <div className="section-wrapper-header">
                        <div className="section-number">3)</div>
                        <div className="section-title">
                          Choisissez un budget à exporter avec la synthèse:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="radio-options-container">
                          <Radio.Group
                            onChange={(e) => {
                              setFieldValue("budgetType", e.target.value);
                              // Reset budgetId if switching to "none"
                              if (e.target.value !== "specific") {
                                setFieldValue("budgetId", null);
                              }
                            }}
                            value={values.budgetType}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            <div className="budget-option-item">
                              <Radio value="none">Aucun budget</Radio>
                            </div>

                            <Space>
                              <Radio value="specific" />
                              <Select
                                value={values.budgetId}
                                disabled={values.budgetType !== "specific"}
                                onChange={(val) =>
                                  setFieldValue("budgetId", val)
                                }
                                style={{ minWidth: 300 }}
                                placeholder="Choisir un budget dans la liste"
                              >
                                {objectivesWithAlertes.map((budget) => (
                                  <Option key={budget.id} value={budget.id}>
                                    {budget.name}
                                  </Option>
                                ))}
                              </Select>
                            </Space>

                            {values.budgetType === "specific" && (
                              <div className="dropdown-suggestion">
                                <div className="suggestion-text">
                                  On propose une liste déroulante avec tous les
                                  budgets existants
                                </div>
                              </div>
                            )}
                          </Radio.Group>

                          {errors.budgetType && touched.budgetType && (
                            <div className="error-message">
                              {errors.budgetType}
                            </div>
                          )}
                          {errors.budgetId && touched.budgetId && (
                            <div className="error-message">
                              {errors.budgetId}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="export-synthesis-section">
                      <div className="section-wrapper-header">
                        <div className="section-number">4)</div>
                        <div className="section-title">
                          Choisissez un filtre au niveau des écritures:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="radio-options-container">
                          <Radio.Group
                            onChange={(e) => {
                              setFieldValue("filterType", e.target.value);
                              // Reset selected filters if switching to "none"
                              if (e.target.value !== "apply") {
                                setFieldValue("selectedFilters", []);
                              }
                            }}
                            value={values.filterType}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            <div className="filter-option-item">
                              <Radio value="none">Aucun filtre</Radio>
                            </div>

                            <div className="filter-option-item">
                              <Radio value="apply">Appliquer un filtre</Radio>

                              {values.filterType === "apply" && (
                                <div className="filter-checkboxes-container">
                                  {filterData.map((filter) => {
                                    const isEqual = (a, b) =>
                                      JSON.stringify(a) === JSON.stringify(b);
                                    const areAllChildrenChecked =
                                      isParentChecked(
                                        filter,
                                        values.selectedFilters
                                      );

                                    return (
                                      <div
                                        key={filter.id}
                                        className="filter-group"
                                      >
                                        <Checkbox
                                          checked={
                                            filter.children
                                              ? areAllChildrenChecked
                                              : values.selectedFilters.some(
                                                  (selected) =>
                                                    isEqual(
                                                      selected,
                                                      filter.filterValues
                                                    )
                                                )
                                          }
                                          onChange={(e) =>
                                            filter.children
                                              ? handleParentFilterChange(
                                                  filter,
                                                  e.target.checked,
                                                  values.selectedFilters,
                                                  setFieldValue
                                                )
                                              : handleFilterChange(
                                                  filter,
                                                  e.target.checked,
                                                  values.selectedFilters,
                                                  setFieldValue
                                                )
                                          }
                                        >
                                          {filter.label}
                                        </Checkbox>

                                        {filter.children && (
                                          <div className="filter-children">
                                            {filter.children.map((child) => (
                                              <Checkbox
                                                key={child.id}
                                                checked={values.selectedFilters.some(
                                                  (selected) =>
                                                    isEqual(
                                                      selected,
                                                      child.filterValues
                                                    )
                                                )}
                                                onChange={(e) =>
                                                  handleFilterChange(
                                                    child,
                                                    e.target.checked,
                                                    values.selectedFilters,
                                                    setFieldValue
                                                  )
                                                }
                                              >
                                                <div
                                                  className="filter-child-label"
                                                  style={{
                                                    background:
                                                      child.background,
                                                  }}
                                                >
                                                  {child.label}
                                                </div>
                                              </Checkbox>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </Radio.Group>

                          {errors.filterType && touched.filterType && (
                            <div className="error-message">
                              {errors.filterType}
                            </div>
                          )}
                          {errors.selectedFilters &&
                            touched.selectedFilters && (
                              <div className="error-message">
                                {errors.selectedFilters}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="export-synthesis-section">
                      <div className="section-wrapper-header">
                        <div className="section-number">5)</div>
                        <div className="section-title">Ne pas exporter :</div>
                      </div>
                      <div className="section-content">
                        <div className="radio-options-container">
                          <div className="filter-option-item">
                            <Radio
                              checked={!!values.excludeMaskedEmptyCategories}
                              onClick={(e) => {
                                e.preventDefault();
                                setFieldValue(
                                  "excludeMaskedEmptyCategories",
                                  !values.excludeMaskedEmptyCategories
                                );
                              }}
                            >
                              Les catégories masquées (si elles sont vides !)
                            </Radio>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="verify-inputs-popup">
                    <Button
                      text={"Annuler"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCancel(resetForm);
                      }}
                      className={"action-button"}
                      htmlType="button"
                      disabled={isSubmitting || exportStatus === "loading"}
                    />
                    <Button
                      text={"Continuer"}
                      htmlType="submit"
                      className={"action-button agree-action-button"}
                      disabled={isSubmitting || exportStatus === "loading"}
                      loading={isSubmitting || exportStatus === "loading"}
                    />
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </HeaderOverlay>
    </Overlay>
  );
}

export default ExportSynthesisResult;
